import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import withApi from '../../components/higherOrderComponents/withApi';
import { readQuery } from '../query';
import ListTools from '../components/ListTools';
import ActiveFilters from '../components/ActiveFilters';
import EmptyRow from '../components/EmptyRow';
import Guid from '../components/Guid';
import Pager from '../components/Pager';
import LookupSelect from '../components/LookupSelect';
import { lookupFacility } from '../lookups';
import StatusPill from '../components/StatusPill';

/**
 * Signups — the searchable list, rebuilt from the prototype.
 *
 * The URL stays the state, exactly as the old page has it: every filter lives in
 * the query string, so a filtered list is a link somebody can paste into Slack.
 * That is worth more than it looks on a page whose whole job is answering "what
 * happened to this booking", and it is why the filters read from `location`
 * rather than from component state.
 *
 * All fifteen filters the old page offers are here. Three of them sit behind
 * "More filters" — the prototype shows a compact row, and these are the ones
 * that get used rarely enough that hiding them costs nothing. Nothing is
 * dropped: a filter that vanishes is a search somebody can no longer run.
 *
 * Facility is a plain id field rather than the old page's async lookup. The
 * lookup is a `react-select` widget the old stylesheet owns, and porting it
 * belongs with the shared components, not with this page. Until then the id
 * still works, which is what the field did anyway.
 */

/**
 * The sorts `SignupsController` can actually do, and only those.
 *
 * It switches on nine keys and falls through to `CreatedAt` for anything else,
 * so an arrow on a column it does not know sorts by signup date while claiming
 * otherwise — the same trap the emails list documents.
 *
 * **Facility is deliberately absent.** The endpoint has a `facility` case, but it
 * orders by `FacilityId` — a GUID. It groups the rows by facility, which looks
 * like it worked, and orders those groups by something nobody can see. An arrow
 * that produces an order no reader can explain is worse than no arrow.
 */
const SORTABLE = {
  Id: 'id',
  User: 'username',
  Email: 'useremail',
  Activity: 'activitydate',
  Court: 'courtname',
  Status: 'status',
  Secret: 'secretcode',
  // Not a case in the switch — it is the `default`, which is `CreatedAt`, which
  // is the signup date. Named for what it sorts by rather than for the branch it
  // happens to take.
  Signup: 'signupdate'
};

const STATUSES = ['Undefined', 'Booked', 'Cancelled'];

// Shown in the row above the fold. The rest live behind "More filters".
// Facility is a `LookupSelect` below rather than a field here: it takes an id,
// and nobody knows a facility by its GUID.
const BASE_FILTERS = [
  { name: 'id', label: 'Id', placeholder: 'Find by id' },
  // `user` is a real free-text search — the endpoint matches it against name,
  // email and phone — so it stays a text field rather than becoming a lookup.
  { name: 'user', label: 'User', placeholder: 'Name, email or phone' }
];

const MORE_FILTERS = [
  { name: 'courtName', label: 'Court', placeholder: 'Filter by court name' },
  { name: 'secretCode', label: 'Secret code', placeholder: 'Filter by secret code' },
  { name: 'externalReference', label: 'External reference', placeholder: 'Filter by reference' },
  { name: 'purchaseId', label: 'Purchase id', placeholder: 'Filter by purchase id' },
  { name: 'signupFrom', label: 'Signup from', type: 'datetime-local' },
  { name: 'signupTo', label: 'Signup to', type: 'datetime-local' },
  { name: 'activityFrom', label: 'Activity from', type: 'datetime-local' },
  { name: 'activityTo', label: 'Activity to', type: 'datetime-local' }
];

const camelToWords = text => {
  const value = text == null ? '' : String(text);
  const spaced = value.replace(/([A-Z])/g, ' $1').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// The API sends CET wall-clock strings. Parsing and reformatting them in the
// browser's zone would shift them, so they are only tidied.
const formatDate = value => (value ? String(value).replace('T', ' ').slice(0, 16) : '—');

// Raw payload keys, so an exported id is the whole id — the table renders them
// short, which is exactly what makes copying out of the table useless.
const EXPORT_COLUMNS = [
  ['id', 'Id'],
  ['status', 'Status'],
  ['facilityName', 'Facility'],
  ['category', 'Category'],
  ['courtName', 'Court'],
  ['duration', 'Duration (min)'],
  ['signupDateCet', 'Signup (CET)'],
  ['activityDateCet', 'Activity (CET)'],
  ['userName', 'User'],
  ['userEmail', 'Email'],
  ['userPhoneNumber', 'Phone'],
  ['isBundle', 'Bundle'],
  ['secretCode', 'Secret code'],
  ['externalReference', 'External reference'],
  ['purchaseId', 'Purchase id'],
  ['confirmations', 'Confirmations']
];

const FILTER_FIELDS = [
  ...BASE_FILTERS,
  { name: 'facilityId', label: 'Facility' },
  { name: 'category', label: 'Category' },
  { name: 'status', label: 'Status' },
  ...MORE_FILTERS,
  { name: 'onlyBundles', label: 'Only bundles', format: () => 'yes' }
];

const SignupsInternal = ({ api, categories = [] }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => readQuery(location.search), [location.search]);

  const [rows, setRows] = useState(null);
  const [pages, setPages] = useState(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(query);
  const [showMore, setShowMore] = useState(false);

  const page = parseInt(query.page, 10) || 1;
  // 15 is what `searchSignups` sends when the filter is empty.
  // The same question the chips answer, so the empty row and the chips
  // can never disagree about whether anything is filtering the list.
  const orderBy = query.orderBy || SORTABLE.Signup;
  const orderDirection = query.orderDirection === 'asc' ? 'asc' : 'desc';

  const filtered = FILTER_FIELDS.some(({ name: key }) => (
    key !== 'page' && key !== 'resultsPerPage' && query[key] !== undefined && query[key] !== ''
  ));

  const perPage = parseInt(query.resultsPerPage, 10) || 15;

  // A filter changed in the URL — usually Back or a pasted link — so the form
  // has to follow it rather than keep whatever was half-typed.
  useEffect(() => {
    setDraft(query);
  }, [query]);

  const load = useCallback(async () => {
    setRows(null);
    try {
      // The endpoint answers `{ signups, pages }` — not an array, and not the
      // `{ success, data }` envelope the rest of this api module uses.
      const data = await api.signups.searchSignups({ filters: query });
      setRows(data?.signups ?? []);
      setPages(typeof data?.pages === 'number' ? data.pages : null);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, query]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = (next, { resetPage = true } = {}) => {
    // The sort survives a search. It is not a filter — the filter row does not
    // hold it and `next` is built from that row — so applying one used to drop
    // the column somebody had just sorted by, silently, back to the default.
    const merged = {
      orderBy: query.orderBy,
      orderDirection: query.orderDirection,
      ...next
    };
    if (resetPage) delete merged.page;

    const search = Object.entries(merged)
      .filter(([, value]) => value !== '' && value != null)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    navigate(search ? `?${search}` : '', { replace: false });
  };

  const set = (name, value) => setDraft(previous => ({ ...previous, [name]: value }));

  // Clicking the sorted column flips the direction; a different column starts
  // newest-or-largest first, which is what is wanted on every column here.
  const sortBy = key => applyFilters({
    ...query,
    orderBy: key,
    orderDirection: orderBy.toLowerCase() === key.toLowerCase() && orderDirection === 'desc' ? 'asc' : 'desc'
  });

  const sortMark = key =>
    (orderBy.toLowerCase() === key.toLowerCase() ? (orderDirection === 'desc' ? ' ↓' : ' ↑') : '');

  const header = (label, key, className) => (
    <th className={className}>
      {key ? (
        <button type="button" className="next-th-sort" onClick={() => sortBy(key)}>
          {label}{sortMark(key)}
        </button>
      ) : label}
    </th>
  );

  const activeMoreCount = MORE_FILTERS.filter(({ name }) => query[name]).length;

  const field = ({ name, label, placeholder, type }) => (
    <label className="next-filter" key={name}>
      <span className="next-filter__label">{label}</span>
      <input
        className="next-input"
        type={type || 'text'}
        value={draft[name] ?? ''}
        placeholder={placeholder}
        onChange={event => set(name, event.target.value)}
      />
    </label>
  );

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header next-page__header--row">
        <div>
          <h1>Signups</h1>
          <p className="next-muted">
            {rows === null
              ? 'Loading…'
              : `Page ${page}${pages ? ` of ${pages.toLocaleString()}` : ''} · ${perPage} per page`}
          </p>
        </div>
        <div className="next-page__actions">
          <button type="button" className="next-btn" onClick={load}>Refresh</button>
        </div>
      </header>

      <section className="next-card">
        <form
          className="next-filters"
          onSubmit={event => {
            event.preventDefault();
            applyFilters(draft);
          }}
        >
          <div className="next-filters__row">
            {BASE_FILTERS.map(field)}

            <LookupSelect
              label="Facility"
              value={draft.facilityId}
              onChange={value => set('facilityId', value)}
              loadOptions={key => lookupFacility(api, key)}
              placeholder="Name or id"
            />

            <label className="next-filter">
              <span className="next-filter__label">Category</span>
              <select
                className="next-input"
                value={draft.category ?? ''}
                onChange={event => set('category', event.target.value)}
              >
                <option value="">Any</option>
                {categories.map(({ Name }) => <option key={Name} value={Name}>{Name}</option>)}
              </select>
            </label>

            <label className="next-filter">
              <span className="next-filter__label">Status</span>
              <select
                className="next-input"
                value={draft.status ?? ''}
                onChange={event => set('status', event.target.value)}
              >
                <option value="">Any</option>
                {STATUSES.map(value => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>

            <div className="next-filters__buttons">
              <button type="submit" className="next-btn next-btn--primary">Search</button>
              <button
                type="button"
                className="next-btn"
                onClick={() => setShowMore(value => !value)}
              >
                {showMore ? 'Fewer filters' : 'More filters'}
                {activeMoreCount > 0 && <span className="next-badge">{activeMoreCount}</span>}
              </button>
            </div>
          </div>

          {showMore && (
            <div className="next-filters__row next-filters__row--more">
              {MORE_FILTERS.map(field)}

              <label className="next-filter next-filter--check">
                <input
                  type="checkbox"
                  checked={draft.onlyBundles === 'true'}
                  onChange={event => set('onlyBundles', event.target.checked ? 'true' : '')}
                />
                <span>Only bundles</span>
              </label>

              <div className="next-filters__buttons">
                <button
                  type="button"
                  className="next-btn next-btn--quiet"
                  onClick={() => {
                    setDraft({});
                    applyFilters({});
                  }}
                >
                  Clear filters
                </button>
              </div>
            </div>
          )}
        </form>
      </section>

      <ActiveFilters
        fields={FILTER_FIELDS}
        query={query}
        onChange={next => { setDraft(next); applyFilters(next); }}
      />

      <ListTools
        screenPath="/next/signups"
        search={location.search}
        columns={EXPORT_COLUMNS}
        rows={rows}
        exportName="signups"
        perPage={perPage}
        // "Every signup for this facility this month" is the export people
        // actually want, and the page on screen is not it.
        fetchPage={next => api.signups
          .searchSignups({ filters: { ...query, page: next } })
          .then(data => data?.signups ?? [])}
      />

      {failed ? (
        <div className="next-note next-note--error">
          Could not load the signups.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      ) : (
        <section className="next-card">
          <div className="next-table-wrap">
            <table className="next-table next-table--wide next-table--stack">
              <thead>
                <tr>
                  {header('Id', SORTABLE.Id)}
                  {/* One column, two sortable fields, like the emails list's
                      From/To — the court is what you sort a facility's day by,
                      and the facility itself cannot be sorted honestly. */}
                  <th>
                    Facility /{' '}
                    <button type="button" className="next-th-sort" onClick={() => sortBy(SORTABLE.Court)}>
                      activity{sortMark(SORTABLE.Court)}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="next-th-sort" onClick={() => sortBy(SORTABLE.User)}>
                      User{sortMark(SORTABLE.User)}
                    </button>
                    {' / '}
                    <button type="button" className="next-th-sort" onClick={() => sortBy(SORTABLE.Email)}>
                      email{sortMark(SORTABLE.Email)}
                    </button>
                  </th>
                  {header('Signup (CET)', SORTABLE.Signup)}
                  {header('Activity (CET)', SORTABLE.Activity)}
                  {header('Status', SORTABLE.Status)}
                  <th>Bundle</th>
                  <th>Confirmations</th>
                  {header('Secret', SORTABLE.Secret, 'next-table__right')}
                </tr>
              </thead>
              <tbody>
                {rows === null && (
                  <tr><td colSpan={9} className="next-muted next-table__note">Loading…</td></tr>
                )}
                {rows !== null && rows.length === 0 && (
                  <EmptyRow
                    columns={9}
                    noun="signup"
                    filtered={filtered}
                    onClear={() => { setDraft({}); applyFilters({}); }}
                  />
                )}
                {(rows ?? []).map(row => (
                  <tr key={row.id}>
                    <td data-label=""><Guid value={row.id} to={`/next/signup/${row.id}`} /></td>
                    <td data-label="Facility / activity">
                      <span className="next-stack">
                        <span className="next-stack__main">{row.facilityName}</span>
                        <span className="next-stack__sub">
                          {[row.category, row.courtName, row.duration && `${row.duration} min`]
                            .filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </td>
                    <td data-label="User">
                      <span className="next-stack">
                        <span className="next-stack__main">{row.userName}</span>
                        <span className="next-stack__sub next-break">
                          {[row.userEmail, row.userPhoneNumber].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </td>
                    <td className="next-nowrap next-muted" data-label="Signup (CET)">{formatDate(row.signupDateCet)}</td>
                    <td className="next-nowrap" data-label="Activity (CET)">{formatDate(row.activityDateCet)}</td>
                    <td data-label="Status"><StatusPill status={row.status}>{camelToWords(row.status)}</StatusPill></td>
                    <td data-label="Bundle">{row.isBundle ? <span className="next-pill next-pill--info">Bundle</span> : <span className="next-muted">—</span>}</td>
                    <td data-label="Confirmations">
                      <span className="next-stack">
                        {String(row.confirmations || '')
                          .split(',')
                          .map(value => value.trim())
                          .filter(Boolean)
                          .map(value => (
                            <Guid key={value} value={value} to={`/next/email/${value}`} />
                          ))}
                      </span>
                    </td>
                    <td className="next-nowrap next-table__right" data-label="Secret">{row.secretCode || <span className="next-muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager
            perPage={perPage}
            onPerPageChange={size => applyFilters({ ...query, resultsPerPage: size })}
            page={page}
            pages={pages}
            hasNext={pages ? page < pages : (rows ?? []).length >= perPage}
            onChange={next => applyFilters({ ...query, page: next }, { resetPage: false })}
          />
        </section>
      )}
    </div>
  );
};

SignupsInternal.propTypes = {
  api: PropTypes.object.isRequired,
  categories: PropTypes.array
};

const Signups = withApi(
  connect(({ categories }) => ({ categories: categories || [] }), {})(SignupsInternal)
);

Signups.requiresLogin = true;

export default Signups;
